import { OwnerCellData } from '@/components/tables/OwnerCell';
import { formatDate, getPipelineIcon } from '@/utils/constants';
import { getObjectChipProps } from '@/utils/objectChip';
import { Chip, Box, IconButton, Paper, Stack, Switch, Typography, useTheme } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import React from 'react';

const AutomationCard = ({
	automation,
	enabled,
	onToggle,
	disabled = false,
	canEdit,
	onEdit,
}) => {
	const theme = useTheme();

	const pipelineIcon = automation.pipeline
		? getPipelineIcon(
			automation.pipeline,
			'28px',
			theme.palette.mode === 'dark' ? { bgColor: 'transparent' } : undefined,
		)
		: null;
	const objectChip = getObjectChipProps(automation.trigger, {
		pipeline: automation.pipeline,
		theme,
	});
	const hasModifiedDate = Boolean(automation.lastModified || automation.lastModifiedBy);
	return (
		<Paper
			elevation={0}
			sx={{
				height: '100%',
				p: 2,
				border: '1px solid',
				borderColor: 'divider',
				borderRadius: 1.5,
				display: 'flex',
				flexDirection: 'column',
				gap: 1.5,
				...(canEdit && {
					cursor: 'pointer',
					'&:hover': { borderColor: 'primary.main' },
				}),
			}}
			onClick={() => {
				if (canEdit) onEdit?.(automation);
			}}
		>
			<Stack direction='row' alignItems='center' justifyContent='space-between'>
				<Stack direction='row' alignItems='center' spacing={1}>
					{pipelineIcon}
					{objectChip && (
						<Chip
							size='small'
							color='primary'
							variant='outlined'
							label={objectChip.label}
							sx={{
								borderColor: objectChip.borderColor,
								color: objectChip.color,
							}}
						/>
					)}
				</Stack>
				<Stack direction='row' alignItems='center' spacing={0.5}>
					{canEdit && (
						<IconButton
						sx={{ color: theme.palette.primary.main }}
						aria-label='edit'
						disabled={disabled}
						edge='start'
							onClick={(e) => {
								e.stopPropagation();
								onEdit?.(automation);
							}}
						>
							<EditIcon fontSize='small' />
						</IconButton>
					)}
					<Switch
						size='small'
						checked={enabled}
						disabled={disabled}
						onChange={(e) => onToggle(automation.id, e.target.checked)}
						onClick={(e) => e.stopPropagation()}
						slotProps={{ input: { 'aria-label': `${automation.title} status` } }}
					/>
				</Stack>
			</Stack>

			<Typography>{automation.description || automation.title}</Typography>

			{hasModifiedDate && (
				<Stack alignItems='flex-end' spacing={0.25} sx={{ mt: 'auto' }}>
					{automation.lastModifiedBy && (
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'flex-end',
								width: '100%',
								'& > .MuiStack-root': {
									width: 'auto',
									justifyContent: 'flex-end',
								},
							}}
						>
							{/* lastModifiedBy is the modifier's Firestore user doc id. */}
							<OwnerCellData userId={automation.lastModifiedBy} color='text.secondary' />
						</Box>
					)}
					{automation.lastModified && (
						<Typography variant='caption' color='text.secondary'>
							{formatDate(automation.lastModified, true)}
						</Typography>
					)}
				</Stack>
			)}
		</Paper>
	);
};

export default AutomationCard;
